import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PageLoader, TableSkeleton } from '../components/Loading';
import { useAuth } from '../context/AuthContext';
import { useDataRefresh } from '../context/DataRefreshContext';
import {
  formatCurrency,
  formatDate,
  SALE_TYPES,
  SALE_SOURCES,
} from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Plus, Pencil, Trash2, Receipt, FileText, Check, ChevronsUpDown, UserPlus, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesPage() {
  const { isMD } = useAuth();
  const { triggerRefresh } = useDataRefresh();
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [customerSearchValue, setCustomerSearchValue] = useState('');
  const [formData, setFormData] = useState({
    property_id: '',
    sale_type: '',
    sale_amount: '',
    sale_date: new Date().toISOString().split('T')[0],
    sale_source: '',
    primary_agent_id: '',
    is_collaborative: false,
    co_agent_ids: [],
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    referral_source_name: '',
    referral_source_phone: '',
    notes: '',
    units_purchased: 1,
  });
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Approval modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [saleToApprove, setSaleToApprove] = useState(null);
  const [approvalData, setApprovalData] = useState({
    agent_commission_rate: '',
    referral_commission_rate: '',
  });

  // Filters
  const [filters, setFilters] = useState({
    sale_source: '',
    agent_id: '',
    sale_type: '',
    date_from: '',
    date_to: '',
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.sale_source) params.append('sale_source', filters.sale_source);
      if (filters.agent_id) params.append('agent_id', filters.agent_id);
      if (filters.sale_type) params.append('sale_type', filters.sale_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const [salesRes, propertiesRes, agentsRes, customersRes] = await Promise.all([
        axios.get(`${API}/sales?${params}`),
        axios.get(`${API}/properties`),
        axios.get(`${API}/agents`),
        axios.get(`${API}/customers`),
      ]);

      setSales(salesRes.data);
      setProperties(propertiesRes.data);
      setAgents(agentsRes.data.filter(a => a.status === 'Active'));
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      property_id: '',
      sale_type: '',
      sale_amount: '',
      sale_date: new Date().toISOString().split('T')[0],
      sale_source: '',
      primary_agent_id: '',
      is_collaborative: false,
      co_agent_ids: [],
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      referral_customer_id: '',
      referral_source_name: '',
      referral_source_phone: '',
      isNewReferral: false,
      notes: '',
      units_purchased: 1,
    });
    setFormErrors({});
    setSelectedSale(null);
    setCustomerSearchValue('');
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
      setAutoSaveTimeout(null);
    }
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (sale) => {
    setSelectedSale(sale);
    setFormData({
      property_id: sale.property_id,
      sale_type: sale.sale_type,
      sale_amount: sale.sale_amount,
      sale_date: sale.sale_date,
      sale_source: sale.sale_source,
      primary_agent_id: sale.primary_agent_id || '',
      is_collaborative: sale.is_collaborative,
      co_agent_ids: sale.co_agent_ids || [],
      customer_id: '',
      customer_name: sale.customer_name,
      customer_phone: sale.customer_phone,
      customer_email: sale.customer_email || '',
      referral_customer_id: sale.referral_customer_id || '',
      referral_source_name: sale.referral_source_name || '',
      referral_source_phone: sale.referral_source_phone || '',
      notes: sale.notes || '',
      units_purchased: sale.units_purchased || 1,
    });
    setCustomerSearchValue(sale.customer_name);
    setFormErrors({});
    setModalOpen(true);
  };

  const openDeleteDialog = (sale) => {
    setSelectedSale(sale);
    setDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.property_id) errors.property_id = 'Property is required';
    if (!formData.sale_type) errors.sale_type = 'Sale type is required';
    if (!formData.sale_amount || formData.sale_amount <= 0) errors.sale_amount = 'Valid amount is required';
    if (!formData.sale_date) errors.sale_date = 'Sale date is required';
    if (!formData.sale_source) errors.sale_source = 'Sale source is required';
    if (!formData.customer_name.trim()) errors.customer_name = 'Customer name is required';
    if (!formData.customer_phone.trim()) errors.customer_phone = 'Customer phone is required';
    
    // Validate units_purchased
    const selectedProperty = properties.find(p => p.id === formData.property_id);
    if (selectedProperty) {
      const availableUnits = selectedProperty.available_units || 1;
      const unitsPurchased = parseInt(formData.units_purchased) || 1;
      if (unitsPurchased < 1) {
        errors.units_purchased = 'At least 1 unit is required';
      } else if (unitsPurchased > availableUnits) {
        errors.units_purchased = `Only ${availableUnits} ${selectedProperty.unit_type || 'unit'}(s) available`;
      }
    }
    
    if (formData.sale_source !== 'MD Initiated' && !formData.primary_agent_id) {
      errors.primary_agent_id = 'Agent is required';
    }
    
    if (formData.sale_source === 'Customer Referral') {
      if (!formData.referral_source_name.trim()) errors.referral_source_name = 'Referral source name is required';
      if (!formData.referral_source_phone.trim()) errors.referral_source_phone = 'Referral source phone is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      // First, ensure the customer exists (find or create)
      const customerRes = await axios.post(`${API}/customers/find-or-create`, {
        name: formData.customer_name,
        phone: formData.customer_phone,
        email: formData.customer_email || null,
      });

      const payload = {
        property_id: formData.property_id,
        sale_type: formData.sale_type,
        sale_amount: parseFloat(formData.sale_amount),
        sale_date: formData.sale_date,
        sale_source: formData.sale_source,
        primary_agent_id: formData.sale_source === 'MD Initiated' ? null : formData.primary_agent_id,
        is_collaborative: formData.is_collaborative,
        co_agent_ids: formData.is_collaborative ? formData.co_agent_ids : [],
        customer_name: formData.customer_name,
        customer_phone: formData.customer_phone,
        customer_email: formData.customer_email || null,
        referral_source_name: formData.sale_source === 'Customer Referral' ? formData.referral_source_name : null,
        referral_source_phone: formData.sale_source === 'Customer Referral' ? formData.referral_source_phone : null,
        notes: formData.notes || null,
        units_purchased: parseInt(formData.units_purchased) || 1,
      };

      if (selectedSale) {
        await axios.put(`${API}/sales/${selectedSale.id}`, payload);
        toast.success('Sale updated successfully');
      } else {
        await axios.post(`${API}/sales`, payload);
        toast.success('Sale created successfully');
      }

      setModalOpen(false);
      resetForm();
      fetchData();
      triggerRefresh(); // Refresh dashboard and other components
    } catch (error) {
      console.error('Error saving sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to save sale');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/sales/${selectedSale.id}`);
      toast.success('Sale deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedSale(null);
      fetchData();
      triggerRefresh(); // Refresh dashboard and other components
    } catch (error) {
      console.error('Error deleting sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete sale');
    }
  };

  const handlePropertyChange = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    setFormData({
      ...formData,
      property_id: propertyId,
      sale_type: property?.sale_type || '',
      sale_amount: property?.listed_price || '',
      units_purchased: 1, // Reset to 1 when property changes
    });
  };

  // Auto-save function for agent edits (agents can't undo, MDs have full control)
  const triggerAutoSave = (updatedData) => {
    // Only auto-save for agents editing existing sales
    if (isMD || !selectedSale) return;
    
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Set new timeout for auto-save (2 seconds delay)
    const timeout = setTimeout(async () => {
      try {
        const payload = {
          property_id: updatedData.property_id,
          sale_type: updatedData.sale_type,
          sale_amount: parseFloat(updatedData.sale_amount) || 0,
          sale_date: updatedData.sale_date,
          sale_source: updatedData.sale_source,
          primary_agent_id: updatedData.sale_source === 'MD Initiated' ? null : updatedData.primary_agent_id,
          is_collaborative: updatedData.is_collaborative,
          co_agent_ids: updatedData.is_collaborative ? updatedData.co_agent_ids : [],
          customer_name: updatedData.customer_name,
          customer_phone: updatedData.customer_phone,
          customer_email: updatedData.customer_email || null,
          referral_source_name: updatedData.sale_source === 'Customer Referral' ? updatedData.referral_source_name : null,
          referral_source_phone: updatedData.sale_source === 'Customer Referral' ? updatedData.referral_source_phone : null,
          notes: updatedData.notes || null,
          units_purchased: parseInt(updatedData.units_purchased) || 1,
        };
        
        await axios.put(`${API}/sales/${selectedSale.id}`, payload);
        toast.success('Changes auto-saved', { duration: 1500 });
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show error toast for auto-save failures
      }
    }, 2000);
    
    setAutoSaveTimeout(timeout);
  };

  // Helper to update form and trigger auto-save for agents
  const updateFormField = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    triggerAutoSave(newFormData);
  };

  const openApprovalModal = (sale) => {
    setSaleToApprove(sale);
    // Get default rates
    const agent = agents.find(a => a.id === sale.primary_agent_id);
    setApprovalData({
      agent_commission_rate: agent?.commission_rate || 5,
      referral_commission_rate: sale.sale_source === 'Customer Referral' ? 2 : '',
    });
    setApprovalModalOpen(true);
  };

  const handleApproveSale = async () => {
    if (!saleToApprove) return;
    
    try {
      const payload = {};
      if (approvalData.agent_commission_rate !== '') {
        payload.agent_commission_rate = parseFloat(approvalData.agent_commission_rate);
      }
      if (approvalData.referral_commission_rate !== '') {
        payload.referral_commission_rate = parseFloat(approvalData.referral_commission_rate);
      }
      
      await axios.post(`${API}/sales/${saleToApprove.id}/approve`, payload);
      toast.success('Sale approved successfully');
      setApprovalModalOpen(false);
      setSaleToApprove(null);
      fetchData();
      triggerRefresh();
    } catch (error) {
      console.error('Error approving sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve sale');
    }
  };

  const handleRejectSale = async (saleId) => {
    if (!window.confirm('Are you sure you want to reject this sale?')) return;
    try {
      await axios.post(`${API}/sales/${saleId}/reject`);
      toast.success('Sale rejected');
      fetchData();
      triggerRefresh();
    } catch (error) {
      console.error('Error rejecting sale:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject sale');
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData({
      ...formData,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone,
      customer_email: customer.email || '',
    });
    setCustomerSearchValue(customer.name);
    setCustomerPopoverOpen(false);
  };

  const handleCreateNewCustomer = () => {
    // Allow user to type a new name
    setFormData({
      ...formData,
      customer_id: '',
      customer_name: customerSearchValue,
      customer_phone: '',
      customer_email: '',
    });
    setCustomerPopoverOpen(false);
  };

  const toggleCoAgent = (agentId) => {
    const newCoAgents = formData.co_agent_ids.includes(agentId)
      ? formData.co_agent_ids.filter(id => id !== agentId)
      : [...formData.co_agent_ids, agentId];
    setFormData({ ...formData, co_agent_ids: newCoAgents });
  };

  // Auto-compute commission preview
  const commissionPreview = useMemo(() => {
    if (!formData.sale_amount || formData.sale_amount <= 0) return null;
    if (formData.sale_source === 'MD Initiated') return null;
    if (!formData.primary_agent_id) return null;

    const primaryAgent = agents.find(a => a.id === formData.primary_agent_id);
    if (!primaryAgent) return null;

    const saleAmount = parseFloat(formData.sale_amount);
    const commissionRate = primaryAgent.commission_rate || 5;
    const totalCommission = saleAmount * (commissionRate / 100);

    if (formData.is_collaborative && formData.co_agent_ids.length > 0) {
      const numAgents = 1 + formData.co_agent_ids.length;
      const sharePerAgent = totalCommission / numAgents;
      return {
        totalCommission,
        commissionRate,
        isCollaborative: true,
        numAgents,
        sharePerAgent,
        primaryAgentName: primaryAgent.full_name,
        coAgents: formData.co_agent_ids.map(id => agents.find(a => a.id === id)?.full_name).filter(Boolean),
      };
    }

    return {
      totalCommission,
      commissionRate,
      isCollaborative: false,
      primaryAgentName: primaryAgent.full_name,
    };
  }, [formData.sale_amount, formData.primary_agent_id, formData.is_collaborative, formData.co_agent_ids, formData.sale_source, agents]);

  // Filter customers based on search
  const filteredCustomers = customers.filter(c => 
    c.name.toLowerCase().includes(customerSearchValue.toLowerCase())
  );

  return (
    <Layout>
      <div className="space-y-6 fade-in" data-testid="sales-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-4xl text-lux-text mb-2">Sales Records</h1>
            <p className="text-lux-muted">Track all property transactions</p>
          </div>
          <Button onClick={openAddModal} className="lux-btn-primary" data-testid="add-sale-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Sale
          </Button>
        </div>

        {/* Filters */}
        <div className="lux-card p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <Select
              value={filters.sale_source || "all"}
              onValueChange={(value) => setFilters({ ...filters, sale_source: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="sale-source-filter">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {SALE_SOURCES.map((source) => (
                  <SelectItem key={source} value={source}>{source}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.agent_id || "all"}
              onValueChange={(value) => setFilters({ ...filters, agent_id: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="sale-agent-filter">
                <SelectValue placeholder="All Agents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>{agent.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.sale_type || "all"}
              onValueChange={(value) => setFilters({ ...filters, sale_type: value === "all" ? "" : value })}
            >
              <SelectTrigger className="lux-input" data-testid="sale-type-filter">
                <SelectValue placeholder="All Sale Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sale Types</SelectItem>
                {SALE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="lux-input"
              placeholder="From date"
              data-testid="sale-date-from-filter"
            />
            <Input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="lux-input"
              placeholder="To date"
              data-testid="sale-date-to-filter"
            />
          </div>
        </div>

        {/* Table */}
        <div className="lux-card overflow-hidden">
          {loading ? (
            <TableSkeleton rows={5} cols={8} />
          ) : sales.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Receipt className="w-16 h-16 text-lux-border mb-4" />
              <p className="text-lux-muted text-lg">No sales records found</p>
              {isMD && (
                <Button onClick={openAddModal} className="lux-btn-secondary mt-4">
                  Record your first sale
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="lux-table-header">
                  <tr>
                    <th className="px-6 py-4 text-left">Sale Code</th>
                    <th className="px-6 py-4 text-left">Property</th>
                    <th className="px-6 py-4 text-right">Amount (₦)</th>
                    <th className="px-6 py-4 text-left">Type</th>
                    <th className="px-6 py-4 text-left">Source</th>
                    <th className="px-6 py-4 text-left">Agent</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Date</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="lux-table-row" data-testid={`sale-row-${sale.id}`}>
                      <td className="px-6 py-4 text-lux-gold font-mono text-sm">
                        {sale.sale_code}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-lux-text font-medium">{sale.property?.name || 'N/A'}</p>
                          <p className="text-xs text-lux-muted">
                            {sale.units_purchased || 1} {sale.property?.unit_type || 'unit'}(s)
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-lux-gold font-semibold">
                        {formatCurrency(sale.sale_amount)}
                      </td>
                      <td className="px-6 py-4 text-lux-muted text-sm">
                        {sale.sale_type}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          sale.sale_source === 'MD Initiated' ? 'bg-purple-900/30 text-purple-400 border border-purple-900/50' :
                          sale.sale_source === 'Agent Sale' ? 'bg-blue-900/30 text-blue-400 border border-blue-900/50' :
                          'bg-amber-900/30 text-amber-400 border border-amber-900/50'
                        }`}>
                          {sale.sale_source}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-lux-muted">
                        {sale.primary_agent?.full_name || 'MD Office'}
                        {sale.is_collaborative && (
                          <span className="text-xs text-lux-gold ml-1">(+{sale.co_agents?.length || 0})</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 w-fit ${
                          sale.approval_status === 'approved' ? 'bg-green-900/30 text-green-400 border border-green-900/50' :
                          sale.approval_status === 'rejected' ? 'bg-red-900/30 text-red-400 border border-red-900/50' :
                          'bg-yellow-900/30 text-yellow-400 border border-yellow-900/50'
                        }`}>
                          {sale.approval_status === 'approved' && <CheckCircle className="w-3 h-3" />}
                          {sale.approval_status === 'rejected' && <XCircle className="w-3 h-3" />}
                          {sale.approval_status === 'pending' && <Clock className="w-3 h-3" />}
                          {sale.approval_status || 'pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-lux-muted text-sm">
                        {formatDate(sale.sale_date)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Approval buttons - MD only, for pending sales */}
                          {isMD && sale.approval_status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openApprovalModal(sale)}
                                className="text-green-400 hover:text-green-300 hover:bg-green-900/30"
                                data-testid={`approve-sale-${sale.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectSale(sale.id)}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                                data-testid={`reject-sale-${sale.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {/* Invoice button - only for approved sales */}
                          {sale.approval_status === 'approved' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/invoices/${sale.id}`)}
                              className="text-lux-muted hover:text-lux-gold"
                              data-testid={`invoice-sale-${sale.id}`}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          )}
                          {isMD && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(sale)}
                                className="text-lux-muted hover:text-lux-gold"
                                data-testid={`edit-sale-${sale.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(sale)}
                                className="text-lux-muted hover:text-red-400"
                                data-testid={`delete-sale-${sale.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-text">
                {selectedSale ? 'Edit Sale' : 'Record New Sale'}
              </DialogTitle>
              <DialogDescription className="text-lux-muted">
                Fill in the sale details below
              </DialogDescription>
            </DialogHeader>
            
            {/* Agent Notice - Show for non-MD users */}
            {!isMD && !selectedSale && (
              <div className="bg-amber-900/20 border border-amber-600/50 rounded-lg p-3 mb-4">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Your sale will be submitted for MD approval before it can be finalized.
                </p>
              </div>
            )}
            
            <div className="space-y-4 py-4">
              {/* Property Selection */}
              <div className="space-y-2">
                <Label className="text-lux-text">Property *</Label>
                <Select
                  value={formData.property_id}
                  onValueChange={handlePropertyChange}
                >
                  <SelectTrigger className={`lux-input ${formErrors.property_id ? 'border-red-500' : ''}`} data-testid="sale-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.filter(prop => prop.available_units > 0 || prop.status === 'Available').map((prop) => (
                      <SelectItem key={prop.id} value={prop.id}>
                        {prop.property_code} - {prop.name} ({prop.available_units || prop.total_units || 1} {prop.unit_type || 'unit'}s available)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.property_id && <p className="text-red-400 text-xs">{formErrors.property_id}</p>}
                {formData.property_id && (() => {
                  const selectedProp = properties.find(p => p.id === formData.property_id);
                  if (selectedProp) {
                    return (
                      <p className="text-lux-muted text-xs">
                        Available: {selectedProp.available_units || selectedProp.total_units || 1} of {selectedProp.total_units || 1} {selectedProp.unit_type || 'unit'}(s) • Price per {selectedProp.unit_type || 'unit'}: ₦{(selectedProp.listed_price || 0).toLocaleString()}
                      </p>
                    );
                  }
                  return null;
                })()}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-lux-text">Sale Type *</Label>
                  <Select
                    value={formData.sale_type}
                    onValueChange={(value) => setFormData({ ...formData, sale_type: value })}
                  >
                    <SelectTrigger className={`lux-input ${formErrors.sale_type ? 'border-red-500' : ''}`} data-testid="sale-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SALE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.sale_type && <p className="text-red-400 text-xs">{formErrors.sale_type}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-lux-text">Units to Purchase *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={(() => {
                      const selectedProp = properties.find(p => p.id === formData.property_id);
                      return selectedProp?.available_units || 999;
                    })()}
                    value={formData.units_purchased}
                    onChange={(e) => {
                      const value = e.target.value;
                      const selectedProp = properties.find(p => p.id === formData.property_id);
                      const pricePerUnit = selectedProp?.listed_price || 0;
                      // Allow empty string during typing, calculate amount if valid number
                      const units = value === '' ? '' : parseInt(value);
                      setFormData({ 
                        ...formData, 
                        units_purchased: value === '' ? '' : units,
                        sale_amount: (units || 0) * pricePerUnit 
                      });
                    }}
                    onBlur={(e) => {
                      // On blur, ensure minimum value of 1
                      const value = parseInt(e.target.value);
                      if (isNaN(value) || value < 1) {
                        const selectedProp = properties.find(p => p.id === formData.property_id);
                        const pricePerUnit = selectedProp?.listed_price || 0;
                        setFormData({ 
                          ...formData, 
                          units_purchased: 1,
                          sale_amount: 1 * pricePerUnit 
                        });
                      }
                    }}
                    className={`lux-input ${formErrors.units_purchased ? 'border-red-500' : ''}`}
                    data-testid="sale-units-input"
                    disabled={!formData.property_id}
                  />
                  {formErrors.units_purchased && <p className="text-red-400 text-xs">{formErrors.units_purchased}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-lux-text">Total Amount (₦) *</Label>
                  <Input
                    type="number"
                    value={formData.sale_amount}
                    onChange={(e) => setFormData({ ...formData, sale_amount: e.target.value })}
                    className={`lux-input ${formErrors.sale_amount ? 'border-red-500' : ''}`}
                    data-testid="sale-amount-input"
                  />
                  {formErrors.sale_amount && <p className="text-red-400 text-xs">{formErrors.sale_amount}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-lux-text">Sale Date *</Label>
                  <Input
                    type="date"
                    value={formData.sale_date}
                    onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
                    className={`lux-input ${formErrors.sale_date ? 'border-red-500' : ''}`}
                    data-testid="sale-date-input"
                  />
                  {formErrors.sale_date && <p className="text-red-400 text-xs">{formErrors.sale_date}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-lux-text">Sale Source *</Label>
                  <Select
                    value={formData.sale_source}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      sale_source: value,
                      primary_agent_id: value === 'MD Initiated' ? '' : formData.primary_agent_id,
                      is_collaborative: value === 'MD Initiated' ? false : formData.is_collaborative,
                    })}
                  >
                    <SelectTrigger className={`lux-input ${formErrors.sale_source ? 'border-red-500' : ''}`} data-testid="sale-source-select">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {SALE_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>{source}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formErrors.sale_source && <p className="text-red-400 text-xs">{formErrors.sale_source}</p>}
                </div>
              </div>

              {/* Agent Selection - Show only if not MD Initiated */}
              {formData.sale_source && formData.sale_source !== 'MD Initiated' && (
                <>
                  <div className="space-y-2">
                    <Label className="text-lux-text">Primary Agent *</Label>
                    <Select
                      value={formData.primary_agent_id}
                      onValueChange={(value) => setFormData({ ...formData, primary_agent_id: value })}
                    >
                      <SelectTrigger className={`lux-input ${formErrors.primary_agent_id ? 'border-red-500' : ''}`} data-testid="sale-agent-select">
                        <SelectValue placeholder="Select agent" />
                      </SelectTrigger>
                      <SelectContent>
                        {agents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>{agent.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.primary_agent_id && <p className="text-red-400 text-xs">{formErrors.primary_agent_id}</p>}
                  </div>

                  {/* Collaborative Toggle */}
                  <div className="flex items-center justify-between p-4 bg-lux-row rounded-lg">
                    <div>
                      <Label className="text-lux-text">Collaborative Sale</Label>
                      <p className="text-lux-muted text-xs">Commission will be split equally among agents</p>
                    </div>
                    <Switch
                      checked={formData.is_collaborative}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_collaborative: checked, co_agent_ids: [] })}
                      data-testid="sale-collaborative-switch"
                    />
                  </div>

                  {/* Co-agents Selection */}
                  {formData.is_collaborative && (
                    <div className="space-y-2">
                      <Label className="text-lux-text">Co-Agents</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {agents.filter(a => a.id !== formData.primary_agent_id).map((agent) => (
                          <button
                            key={agent.id}
                            type="button"
                            onClick={() => toggleCoAgent(agent.id)}
                            className={`p-3 rounded-lg text-left text-sm transition-all ${
                              formData.co_agent_ids.includes(agent.id)
                                ? 'bg-lux-gold/20 border border-lux-gold text-lux-gold'
                                : 'bg-lux-row border border-lux-border text-lux-muted hover:border-lux-gold/50'
                            }`}
                            data-testid={`co-agent-${agent.id}`}
                          >
                            {agent.full_name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Commission Preview - Auto-computed */}
              {commissionPreview && (
                <div className="p-4 bg-green-900/10 border border-green-900/30 rounded-lg">
                  <h4 className="text-green-400 text-sm font-medium mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Commission Preview (Auto-calculated)
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-lux-muted">Sale Amount:</span>
                      <span className="text-lux-text">{formatCurrency(parseFloat(formData.sale_amount))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-lux-muted">Commission Rate:</span>
                      <span className="text-lux-text">{commissionPreview.commissionRate}%</span>
                    </div>
                    <div className="flex justify-between border-t border-green-900/30 pt-2">
                      <span className="text-lux-muted">Total Commission:</span>
                      <span className="text-green-400 font-semibold">{formatCurrency(commissionPreview.totalCommission)}</span>
                    </div>
                    {commissionPreview.isCollaborative && (
                      <>
                        <div className="flex justify-between text-xs">
                          <span className="text-lux-muted">Split between {commissionPreview.numAgents} agents:</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-lux-muted">{commissionPreview.primaryAgentName} (Primary):</span>
                          <span className="text-green-400">{formatCurrency(commissionPreview.sharePerAgent)}</span>
                        </div>
                        {commissionPreview.coAgents.map((name, i) => (
                          <div key={i} className="flex justify-between">
                            <span className="text-lux-muted">{name}:</span>
                            <span className="text-green-400">{formatCurrency(commissionPreview.sharePerAgent)}</span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Referral Source - Show only if Customer Referral */}
              {formData.sale_source === 'Customer Referral' && (
                <div className="p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                  <h4 className="text-amber-400 text-sm font-medium mb-4 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Referral Source (Person who referred this deal)
                  </h4>
                  <div className="space-y-4">
                    {/* Option to select existing customer or enter new */}
                    <div className="flex gap-4 mb-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="referralType"
                          checked={!formData.isNewReferral}
                          onChange={() => setFormData({ ...formData, isNewReferral: false, referral_customer_id: '', referral_source_name: '', referral_source_phone: '' })}
                          className="accent-lux-gold"
                        />
                        <span className="text-lux-text text-sm">Existing Customer</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="referralType"
                          checked={formData.isNewReferral}
                          onChange={() => setFormData({ ...formData, isNewReferral: true, referral_customer_id: '' })}
                          className="accent-lux-gold"
                        />
                        <span className="text-lux-text text-sm">New Referrer</span>
                      </label>
                    </div>

                    {/* Existing customer dropdown */}
                    {!formData.isNewReferral && (
                      <div className="space-y-2">
                        <Label className="text-lux-text">Select Referring Customer *</Label>
                        <Select
                          value={formData.referral_customer_id || ''}
                          onValueChange={(value) => {
                            const customer = customers.find(c => c.id === value);
                            setFormData({ 
                              ...formData, 
                              referral_customer_id: value,
                              referral_source_name: customer?.name || '',
                              referral_source_phone: customer?.phone || ''
                            });
                          }}
                        >
                          <SelectTrigger className={`lux-input ${formErrors.referral_source_name ? 'border-red-500' : ''}`} data-testid="referral-customer-select">
                            <SelectValue placeholder="Select customer who made the referral" />
                          </SelectTrigger>
                          <SelectContent>
                            {customers.map((customer) => (
                              <SelectItem key={customer.id} value={customer.id}>
                                <div className="flex flex-col">
                                  <span>{customer.name}</span>
                                  <span className="text-xs text-lux-muted">{customer.phone}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.referral_source_name && <p className="text-red-400 text-xs">Please select a referring customer</p>}
                      </div>
                    )}

                    {/* Manual entry for new referrer */}
                    {formData.isNewReferral && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-lux-text">Referrer Name *</Label>
                          <Input
                            value={formData.referral_source_name}
                            onChange={(e) => setFormData({ ...formData, referral_source_name: e.target.value })}
                            className={`lux-input ${formErrors.referral_source_name ? 'border-red-500' : ''}`}
                            placeholder="Name of the person who referred"
                            data-testid="referral-name-input"
                          />
                          {formErrors.referral_source_name && <p className="text-red-400 text-xs">{formErrors.referral_source_name}</p>}
                        </div>
                        <div className="space-y-2">
                          <Label className="text-lux-text">Referrer Phone *</Label>
                          <Input
                            value={formData.referral_source_phone}
                            onChange={(e) => setFormData({ ...formData, referral_source_phone: e.target.value })}
                            className={`lux-input ${formErrors.referral_source_phone ? 'border-red-500' : ''}`}
                            placeholder="Referrer's phone number"
                            data-testid="referral-phone-input"
                          />
                          {formErrors.referral_source_phone && <p className="text-red-400 text-xs">{formErrors.referral_source_phone}</p>}
                        </div>
                      </div>
                    )}

                    {formData.referral_source_name && (
                      <p className="text-xs text-lux-muted mt-2">
                        Referrer: {formData.referral_source_name} {formData.referral_source_phone && `(${formData.referral_source_phone})`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Customer Details */}
              <div className="border-t border-lux-border pt-4 mt-4">
                <h3 className="text-lux-gold text-sm font-medium mb-4">Customer Details (The Buyer)</h3>
                <div className="space-y-4">
                  {/* Customer Name with Combobox */}
                  <div className="space-y-2">
                    <Label className="text-lux-text">Customer Name *</Label>
                    <Popover open={customerPopoverOpen} onOpenChange={setCustomerPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={customerPopoverOpen}
                          className={cn(
                            "w-full justify-between lux-input text-left font-normal",
                            !formData.customer_name && "text-lux-muted",
                            formErrors.customer_name && "border-red-500"
                          )}
                          data-testid="customer-name-combobox"
                        >
                          {formData.customer_name || "Select or type customer name..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0 bg-lux-card border-lux-border" align="start">
                        <Command className="bg-lux-card">
                          <CommandInput 
                            placeholder="Search or type new customer..." 
                            value={customerSearchValue}
                            onValueChange={setCustomerSearchValue}
                            className="text-lux-text"
                          />
                          <CommandList>
                            <CommandEmpty className="p-4 text-center">
                              <p className="text-lux-muted text-sm mb-2">No customer found</p>
                              {customerSearchValue && (
                                <Button 
                                  onClick={handleCreateNewCustomer}
                                  className="lux-btn-secondary text-sm"
                                  size="sm"
                                >
                                  <UserPlus className="w-3 h-3 mr-1" />
                                  Add "{customerSearchValue}" as new customer
                                </Button>
                              )}
                            </CommandEmpty>
                            <CommandGroup>
                              {filteredCustomers.map((customer) => (
                                <CommandItem
                                  key={customer.id}
                                  value={customer.name}
                                  onSelect={() => handleCustomerSelect(customer)}
                                  className="cursor-pointer hover:bg-lux-row"
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      formData.customer_name === customer.name ? "opacity-100 text-lux-gold" : "opacity-0"
                                    )}
                                  />
                                  <div>
                                    <p className="text-lux-text">{customer.name}</p>
                                    <p className="text-lux-muted text-xs">{customer.phone}</p>
                                  </div>
                                </CommandItem>
                              ))}
                              {customerSearchValue && !filteredCustomers.some(c => c.name.toLowerCase() === customerSearchValue.toLowerCase()) && (
                                <CommandItem
                                  onSelect={handleCreateNewCustomer}
                                  className="cursor-pointer hover:bg-lux-row border-t border-lux-border mt-1 pt-2"
                                >
                                  <UserPlus className="mr-2 h-4 w-4 text-lux-gold" />
                                  <span className="text-lux-text">Add "{customerSearchValue}" as new customer</span>
                                </CommandItem>
                              )}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    {formErrors.customer_name && <p className="text-red-400 text-xs">{formErrors.customer_name}</p>}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-lux-text">Customer Phone *</Label>
                      <Input
                        value={formData.customer_phone}
                        onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                        className={`lux-input ${formErrors.customer_phone ? 'border-red-500' : ''}`}
                        placeholder="Customer phone"
                        data-testid="sale-customer-phone-input"
                      />
                      {formErrors.customer_phone && <p className="text-red-400 text-xs">{formErrors.customer_phone}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-lux-text">Customer Email</Label>
                      <Input
                        type="email"
                        value={formData.customer_email}
                        onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                        className="lux-input"
                        placeholder="Customer email (optional)"
                        data-testid="sale-customer-email-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-lux-text">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="lux-input min-h-[80px]"
                  placeholder="Additional notes..."
                  data-testid="sale-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setModalOpen(false)}
                className="lux-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={saving}
                className="lux-btn-primary"
                data-testid="save-sale-btn"
              >
                {saving ? 'Saving...' : selectedSale ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-lux-card border-lux-border">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-lux-text">Delete Sale</AlertDialogTitle>
              <AlertDialogDescription className="text-lux-muted">
                Are you sure you want to delete sale "{selectedSale?.sale_code}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="lux-btn-secondary">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Approval Modal with Commission Settings */}
        <Dialog open={approvalModalOpen} onOpenChange={setApprovalModalOpen}>
          <DialogContent className="bg-lux-card border-lux-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-lux-gold">Approve Sale</DialogTitle>
              <DialogDescription className="text-lux-muted">
                Set commission rates before approving this sale
              </DialogDescription>
            </DialogHeader>
            {saleToApprove && (
              <div className="space-y-4 py-4">
                {/* Sale Summary */}
                <div className="p-4 bg-lux-row rounded-lg space-y-2">
                  <p className="text-lux-text font-medium">{saleToApprove.property?.name}</p>
                  <p className="text-lux-muted text-sm">
                    {saleToApprove.units_purchased || 1} {saleToApprove.property?.unit_type || 'unit'}(s) • {formatCurrency(saleToApprove.sale_amount)}
                  </p>
                  <p className="text-lux-muted text-sm">
                    Customer: {saleToApprove.customer_name}
                  </p>
                  {saleToApprove.primary_agent && (
                    <p className="text-lux-muted text-sm">
                      Agent: {saleToApprove.primary_agent.full_name}
                    </p>
                  )}
                </div>

                {/* Agent Commission Rate */}
                {saleToApprove.primary_agent_id && (
                  <div className="space-y-2">
                    <Label className="text-lux-text">Agent Commission Rate (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={approvalData.agent_commission_rate}
                        onChange={(e) => setApprovalData({ ...approvalData, agent_commission_rate: e.target.value })}
                        className="lux-input w-24"
                        data-testid="approval-agent-commission-input"
                      />
                      <span className="text-lux-muted text-sm">
                        = {formatCurrency(saleToApprove.sale_amount * (parseFloat(approvalData.agent_commission_rate) || 0) / 100)} commission
                      </span>
                    </div>
                  </div>
                )}

                {/* Referral Commission Rate (only if Customer Referral) */}
                {saleToApprove.sale_source === 'Customer Referral' && (
                  <div className="space-y-2">
                    <Label className="text-lux-text">Customer Referral Commission (%)</Label>
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={approvalData.referral_commission_rate}
                        onChange={(e) => setApprovalData({ ...approvalData, referral_commission_rate: e.target.value })}
                        className="lux-input w-24"
                        data-testid="approval-referral-commission-input"
                      />
                      <span className="text-lux-muted text-sm">
                        = {formatCurrency(saleToApprove.sale_amount * (parseFloat(approvalData.referral_commission_rate) || 0) / 100)} for referrer
                      </span>
                    </div>
                    <p className="text-xs text-lux-muted">
                      Referrer: {saleToApprove.referral_source_name} ({saleToApprove.referral_source_phone})
                    </p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setApprovalModalOpen(false);
                  setSaleToApprove(null);
                }}
                className="lux-btn-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleApproveSale}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-approve-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve Sale
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
